import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {
  attachDocumentToClaim,
  fetchClaim,
  generateAppealLetter,
  getAppealTemplateConfig,
  listClaimAppeals,
  listClaimDocuments,
  setClaimBillingProvider,
} from '../../api/claimsClient';
import {
  createDraft,
  discardDraft,
  getAppealableLines,
  getTicket,
  submitDraft,
  updateDraft,
} from '../../api/lineItemAppealApi';
import { getClaimActionEligibility, reconcileClaimAction, refreshReconsiderationStatus } from '../../api/claimActionsApi';
import { decideGate, shouldReconcileOnFailure } from './gateDecision';
import ConfidenceDot from '../../components/lineItemAppeal/ConfidenceDot';
import DocumentLibraryModal from '../../components/lineItemAppeal/DocumentLibraryModal';
import InDisputeBar from '../../components/lineItemAppeal/InDisputeBar';
import LetterEditor from '../../components/lineItemAppeal/LetterEditor';
import LineItemsTable from '../../components/lineItemAppeal/LineItemsTable';
import BillingProviderPicker from '../../components/lineItemAppeal/BillingProviderPicker';
import CompleteProfilePrompt from '../../components/lineItemAppeal/CompleteProfilePrompt';

const DENIAL_TEMPLATE_NAMES = {
  medical_necessity: 'Medical Necessity Denial - LLM Prompt',
  authorization: 'Prior Authorization Denial - LLM Prompt',
  coding: 'Coding Error - LLM Prompt',
  documentation: 'Insufficient Documentation - LLM Prompt',
  eligibility: 'Out-of-Network Provider - LLM Prompt',
  // No dedicated seeded templates exist for billing, duplicate, or timely filing.
  // Keep these explicit fallbacks aligned with the legacy claim-management flow.
  billing: 'Insufficient Documentation - LLM Prompt',
  duplicate: 'Insufficient Documentation - LLM Prompt',
  timely_filing: 'Prior Authorization Denial - LLM Prompt',
  benefit_exclusion: 'Experimental/Investigational Denial - LLM Prompt',
  other: 'Medical Necessity Denial - LLM Prompt',
};

function selectAppealTemplateByCategory(templates, denialCategory) {
  const targetName = DENIAL_TEMPLATE_NAMES[denialCategory] || DENIAL_TEMPLATE_NAMES.other;
  return (
    templates.find((t) => t.name === targetName)
    || templates.find((t) => t.name === DENIAL_TEMPLATE_NAMES.other)
    || templates.find((t) => t.is_active !== false)
    || templates[0]
  );
}

const STRATEGY_LABELS = {
  reconsideration: 'Reconsideration',
  corrected_claim: 'Corrected Claim',
  first_level: 'First-Level Appeal',
};

const LEGACY_RECON_REASON = 'INCORRECT_PAYMENT';

const AUTOSAVE_DELAY_MS = 1500;
// Statuses that fully lock the draft from further editing. A failed submission
// is intentionally NOT locked: the backend allows re-submitting from
// 'submission_failed', so the user can correct and retry.
const LOCKED_STATUSES = new Set(['submitted', 'discarded']);
// Statuses that end the post-submit poll (worker has reached a final outcome).
const SUBMIT_RESULT_STATUSES = new Set(['submitted', 'submission_failed', 'discarded']);
const SUBMIT_POLL_MS = 3000;
const SUBMIT_POLL_MAX_ATTEMPTS = 40; // ~2 minutes before we stop spinning
const REASON_REFRESH_POLL_MS = 2000;
const REASON_REFRESH_MAX_ATTEMPTS = 10;
const LETTER_POLL_MS = 3000;
const LETTER_TERMINAL_STATUSES = new Set(['generated', 'failed', 'error']);

function SectionHeader({ number, title }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor: 'var(--ab-fg, #000)',
          color: 'var(--ab-fg-inverse, #fff)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: '0.85rem',
          flexShrink: 0,
        }}
      >
        {number}
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
        {title}
      </Typography>
    </Box>
  );
}

function Section({ number, title, children }) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 3, mb: 3, borderColor: 'var(--ab-border-soft, #A0ABBB)', borderRadius: 2 }}
    >
      <SectionHeader number={number} title={title} />
      {children}
    </Paper>
  );
}

function GateKV({ label, value, mono }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2" sx={{ fontFamily: mono ? 'monospace' : 'inherit' }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

function mergeTicketDetail(previous, next) {
  if (!previous || !next) return next || previous;
  const merged = { ...previous, ...next };
  [
    'reason_choices',
    'document_type_choices',
    'available_documents',
    'available_lines',
    'operator_complete',
  ].forEach((key) => {
    if (next[key] === undefined && previous[key] !== undefined) {
      merged[key] = previous[key];
    }
  });
  return merged;
}

function deriveReasonChoices(ticket) {
  const apiChoices = (ticket?.reason_choices || [])
    .filter((choice) => choice?.value)
    .map((choice) => ({ value: choice.value, label: choice.label || choice.value }));

  // The backend's one-item legacy fallback is not an Optum allowedReconReasons
  // value. Hide it here so Section 5 only presents payer-valid choices.
  if (apiChoices.length === 1 && apiChoices[0].value === LEGACY_RECON_REASON) {
    return [];
  }
  return apiChoices.filter((choice) => choice.value !== LEGACY_RECON_REASON);
}

function selectReasonValue(currentValue, choices) {
  const current = (currentValue || '').trim();
  if (choices.length === 0) {
    return current && current !== LEGACY_RECON_REASON ? current : '';
  }
  if (choices.some((choice) => choice.value === current)) {
    return current;
  }
  return choices[0].value;
}

// Hard-gate panel rendered INSTEAD of the appeal form when the claim already has
// an open reconsideration (NOT_CREATABLE / allowed_action='Update' / submitted).
// The single primary action reconciles (when not yet update-ready), polls until
// the ticket is update-ready, then routes to the existing TicketDetailPage flow.
function ExistingReconGate({ existingTicket, busy, error, onPrimary }) {
  const ticket = existingTicket || {};
  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        Line-Item Appeal
      </Typography>
      <Paper
        variant="outlined"
        sx={{ p: 3, borderColor: 'var(--ab-border-soft, #A0ABBB)', borderRadius: 2 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            This claim already has an open reconsideration
          </Typography>
          {(ticket.ticket_status || ticket.status) && (
            <Chip
              label={ticket.ticket_status || ticket.status}
              size="small"
              variant="outlined"
              sx={{ textTransform: 'capitalize' }}
            />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A reconsideration ticket already exists with the payer, so a new submission cannot be created.
          Continue in the Update flow to add comments or documents to the existing ticket.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 3 }}>
          <GateKV label="Ticket #" value={ticket.ticket_number} mono />
          <GateKV label="Status" value={ticket.ticket_status || ticket.status} />
          <GateKV label="Outcome" value={ticket.ticket_outcome} />
          <GateKV label="Allowed Action" value={ticket.allowed_action} />
          <GateKV label="Closed" value={ticket.is_closed ? 'Yes' : 'No'} />
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Button
          variant="contained"
          onClick={onPrimary}
          disabled={busy}
          sx={{ bgcolor: 'var(--ab-fg, #000)', color: 'var(--ab-fg-inverse, #fff)', '&:hover': { bgcolor: '#333' } }}
        >
          {busy && <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />}
          {busy ? 'Preparing update…' : 'Update Existing Reconsideration'}
        </Button>
      </Paper>
    </Box>
  );
}

const GATE_POLL_MS = 3000;
const GATE_POLL_MAX_ATTEMPTS = 40;

export default function LineItemAppealPage() {
  const { claimId } = useParams();
  const navigate = useNavigate();

  const [linesData, setLinesData] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Outcome of a submission attempt: { severity: 'success'|'error'|'info', message }
  const [submitResult, setSubmitResult] = useState(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [claim, setClaim] = useState(null);
  // Explicit refresh key (CLAUDE.md frontend safety): bumping it re-runs the
  // load effect to refetch the claim after a billing-provider tie change.
  const [claimRefreshKey, setClaimRefreshKey] = useState(0);
  // Slice C2: structured submit block { missing, billing_provider_profile_id }.
  const [submitBlock, setSubmitBlock] = useState(null);
  // Eligibility gate: when the claim already has an open reconsideration the
  // appeal form is hard-gated behind ExistingReconGate (no Create offered).
  const [eligibility, setEligibility] = useState(null);
  const [gateBusy, setGateBusy] = useState(false);
  const [gateError, setGateError] = useState(null);
  const [supportingDocs, setSupportingDocs] = useState([]);
  const [selectedDocIds, setSelectedDocIds] = useState(new Set());
  const [appealLetters, setAppealLetters] = useState([]);
  const [appealTemplates, setAppealTemplates] = useState([]);
  const [generatingLetter, setGeneratingLetter] = useState(false);
  const [letterError, setLetterError] = useState(null);
  const [letterNotice, setLetterNotice] = useState(null);
  const [libraryOpen, setLibraryOpen] = useState(false);

  // Letter editor state
  const [activeAppeal, setActiveAppeal] = useState(null); // { id, appeal_content, appeal_letter }
  // Structured-letter generation status handed to LetterEditor/LetterSectionsPanel
  // so the panel shows the "Generating…" state and refetches sections once on the
  // generating -> generated edge (plan 06-09). 'idle' | 'generating' | 'generated'.
  const [letterGenStatus, setLetterGenStatus] = useState('idle');

  // selectedLines: { [lineId]: amountOwed }
  const [selectedLines, setSelectedLines] = useState({});
  const [appealStrategy, setAppealStrategy] = useState('');
  const [reconsiderationReason, setReconsiderationReason] = useState('');
  const [providerComments, setProviderComments] = useState('');

  // Dirty editor guard — local textarea is source of truth while dirty
  const commentsDirtyRef = useRef(false);
  const autosaveTimerRef = useRef(null);
  const draftBootstrappedRef = useRef(false);
  const ticketIdRef = useRef(null);
  const ticketRef = useRef(null);
  const reasonRefreshPollRef = useRef(null);
  const reasonRefreshAttemptsRef = useRef(0);
  const letterPollRef = useRef(null);
  const submitPollRef = useRef(null);
  const submitAttemptsRef = useRef(0);
  const gatePollRef = useRef(null);
  const gateAttemptsRef = useRef(0);
  // Keep latest navigate in a ref for async gate-poll closures (effect safety).
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  // Keep latest callbacks in refs to avoid stale closures in autosave
  const selectedLinesRef = useRef(selectedLines);
  const providerCommentsRef = useRef(providerComments);
  const appealStrategyRef = useRef(appealStrategy);
  const reconsiderationReasonRef = useRef(reconsiderationReason);
  const selectedDocIdsRef = useRef(selectedDocIds);
  ticketRef.current = ticket;
  selectedLinesRef.current = selectedLines;
  providerCommentsRef.current = providerComments;
  appealStrategyRef.current = appealStrategy;
  reconsiderationReasonRef.current = reconsiderationReason;
  selectedDocIdsRef.current = selectedDocIds;

  const applyTicket = useCallback((nextTicket) => {
    setTicket((previous) => {
      const merged = mergeTicketDetail(previous, nextTicket);
      ticketRef.current = merged;
      return merged;
    });
  }, []);

  const stopReasonRefreshPoll = useCallback(() => {
    if (reasonRefreshPollRef.current) {
      clearInterval(reasonRefreshPollRef.current);
      reasonRefreshPollRef.current = null;
    }
  }, []);

  const startReasonRefreshPoll = useCallback((ticketId) => {
    stopReasonRefreshPoll();
    reasonRefreshAttemptsRef.current = 0;
    reasonRefreshPollRef.current = setInterval(async () => {
      reasonRefreshAttemptsRef.current += 1;
      try {
        const fresh = await getTicket(ticketId);
        applyTicket(fresh);
        if (deriveReasonChoices(fresh).length > 0) {
          stopReasonRefreshPoll();
          return;
        }
      } catch (_e) {
        // Non-blocking: submit-time worker refresh is still authoritative.
      }
      if (reasonRefreshAttemptsRef.current >= REASON_REFRESH_MAX_ATTEMPTS) {
        stopReasonRefreshPoll();
      }
    }, REASON_REFRESH_POLL_MS);
  }, [applyTicket, stopReasonRefreshPoll]);

  useEffect(() => {
    if (!claimId) return;
    setLoading(true);
    Promise.all([
      getAppealableLines(claimId),
      fetchClaim(claimId).catch(() => null),
      listClaimDocuments(claimId).catch(() => []),
      listClaimAppeals(claimId).catch(() => []),
      getAppealTemplateConfig().catch(() => ({ templates: [] })),
      getClaimActionEligibility(claimId).catch(() => null),
    ])
      .then(([data, claimData, docs, letters, templateConfig, eligibilityData]) => {
        setLinesData(data);
        setClaim(claimData);
        setEligibility(eligibilityData);
        setSupportingDocs(Array.isArray(docs) ? docs : []);
        const letterList = Array.isArray(letters) ? letters : [];
        setAppealLetters(letterList);
        setAppealTemplates(Array.isArray(templateConfig?.templates) ? templateConfig.templates : []);
        const preselect = {};
        (data.lines || []).forEach((ln) => {
          if (!ln.appeal_locked && ln.status !== 'paid' && parseFloat(ln.amount_owed) > 0) {
            preselect[ln.id] = ln.amount_owed;
          }
        });
        setSelectedLines(preselect);
        if (data.recommendation?.recommended_strategy) {
          setAppealStrategy(data.recommendation.recommended_strategy);
        }
        // If there's already a generated letter, load it into the editor
        const generated = letterList.find((l) => l.status === 'generated' && l.appeal_content);
        if (generated) setActiveAppeal(generated);
      })
      .catch(() => setError('Failed to load service lines for this claim.'))
      .finally(() => setLoading(false));
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
      if (letterPollRef.current) clearInterval(letterPollRef.current);
      if (submitPollRef.current) clearInterval(submitPollRef.current);
      if (reasonRefreshPollRef.current) clearInterval(reasonRefreshPollRef.current);
      if (gatePollRef.current) clearInterval(gatePollRef.current);
    };
    // claimRefreshKey is an explicit refresh trigger (not a parent callback);
    // the refetch updates `claim` only and never clobbers dirty local edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId, claimRefreshKey]);

  useEffect(() => {
    if (!claimId || loading || error || !linesData || draftBootstrappedRef.current) return;
    if (decideGate(eligibility).gate) return;
    draftBootstrappedRef.current = true;
    createDraft(claimId)
      .then((created) => {
        ticketIdRef.current = created.id;
        applyTicket(created);
        if (deriveReasonChoices(created).length === 0) {
          refreshReconsiderationStatus(created.id)
            .then(() => startReasonRefreshPoll(created.id))
            .catch(() => {});
        }
      })
      .catch(() => {
        draftBootstrappedRef.current = false;
      });
  }, [applyTicket, claimId, eligibility, error, linesData, loading, startReasonRefreshPoll]);

  const reasonChoiceKey = (ticket?.reason_choices || [])
    .map((choice) => `${choice?.value || ''}:${choice?.label || ''}`)
    .join('|');

  useEffect(() => {
    const choices = deriveReasonChoices(ticket);
    const nextValue = selectReasonValue(ticket?.request_reconsideration_reason, choices);
    if (nextValue !== reconsiderationReasonRef.current) {
      reconsiderationReasonRef.current = nextValue;
      setReconsiderationReason(nextValue);
    }
  }, [reasonChoiceKey, ticket]);

  // Persist draft: lazy create then patch
  const persistDraft = useCallback(async (patch) => {
    setSaving(true);
    setSaveError(null);
    try {
      let tid = ticketIdRef.current;
      if (!tid) {
        const created = await createDraft(claimId);
        tid = created.id;
        ticketIdRef.current = tid;
        applyTicket(created);
        try {
          const hydrated = await getTicket(tid);
          applyTicket(hydrated);
        } catch (_e) {
          // Non-fatal: the PATCH below can still save draft fields.
        }
      }
      const reasonChoices = deriveReasonChoices(ticketRef.current);
      const requestReason = selectReasonValue(reconsiderationReasonRef.current, reasonChoices);
      const lines = Object.entries(selectedLinesRef.current).map(([id, amount]) => ({
        optum_line_snapshot_id: id,
        line_amount_owed: amount,
      }));
      const updated = await updateDraft(tid, {
        lines,
        // TODO(optum #2, deferred): initial line-item submission still sends bare
        // document_ids (documentTypeCode defaults to M1). Switch to typed
        // `documents: [{document_id, document_type_code}]` once Optum confirms the
        // requirement — see compatibility review finding #2.
        document_ids: Array.from(selectedDocIdsRef.current),
        provider_comments: providerCommentsRef.current,
        appeal_strategy: appealStrategyRef.current,
        ...(requestReason ? { request_reconsideration_reason: requestReason } : {}),
        ...patch,
      });
      applyTicket(updated);
      return updated;
    } catch (_e) {
      setSaveError('Auto-save failed. Your changes are still in this form.');
      return null;
    } finally {
      setSaving(false);
    }
  }, [applyTicket, claimId]);

  const scheduleAutosave = useCallback(() => {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => persistDraft({}), AUTOSAVE_DELAY_MS);
  }, [persistDraft]);

  // Slice C1: persist the chosen billing provider, then bump claimRefreshKey so
  // the load effect refetches the claim (resolver-driven Billing NPI / tie update).
  const handleBillingProviderSave = useCallback(async (profileId) => {
    await setClaimBillingProvider(claimId, profileId);
    setClaimRefreshKey((k) => k + 1);
  }, [claimId]);

  const handleLineToggle = (line) => {
    setSelectedLines((prev) => {
      const next = { ...prev };
      if (next[line.id]) {
        delete next[line.id];
      } else {
        next[line.id] = line.amount_owed || '0.00';
      }
      return next;
    });
    scheduleAutosave();
  };

  const handleStrategyChange = (value) => {
    setAppealStrategy(value);
    scheduleAutosave();
  };

  const handleReconsiderationReasonChange = (value) => {
    reconsiderationReasonRef.current = value;
    setReconsiderationReason(value);
    scheduleAutosave();
  };

  const handleCommentsChange = (e) => {
    commentsDirtyRef.current = true;
    setProviderComments(e.target.value);
    scheduleAutosave();
  };

  const handleSaveAndClose = async () => {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    await persistDraft({});
    navigate(`/claims/${claimId}`);
  };

  const handleDiscard = async () => {
    setDiscardOpen(false);
    try {
      if (ticketIdRef.current) {
        await discardDraft(ticketIdRef.current);
      }
      navigate(`/claims/${claimId}`);
    } catch (_e) {
      setSaveError('Failed to discard. Please try again.');
    }
  };

  // ── Existing-reconsideration gate ──────────────────────────────────────────
  const stopGatePoll = useCallback(() => {
    if (gatePollRef.current) {
      clearInterval(gatePollRef.current);
      gatePollRef.current = null;
    }
  }, []);

  // Primary gate action: route to the existing ticket's Update flow. If the
  // ticket is not yet update-ready, reconcile first (makes a NOT_CREATABLE ticket
  // update-ready), poll eligibility until ready, then navigate.
  const goToUpdate = useCallback(async (gate) => {
    if (!gate?.ticketId) return;
    const target = `/claims/${claimId}/ticket/${gate.ticketId}`;
    if (gate.ticketReady) {
      navigateRef.current(target);
      return;
    }
    setGateBusy(true);
    setGateError(null);
    try {
      await reconcileClaimAction(claimId);
    } catch (_e) {
      setGateBusy(false);
      setGateError('Could not prepare the update. Please try again.');
      return;
    }
    stopGatePoll();
    gateAttemptsRef.current = 0;
    gatePollRef.current = setInterval(async () => {
      gateAttemptsRef.current += 1;
      try {
        const latest = await getClaimActionEligibility(claimId);
        const decision = decideGate(latest);
        if (decision.gate && decision.ticketReady) {
          stopGatePoll();
          setGateBusy(false);
          navigateRef.current(`/claims/${claimId}/ticket/${decision.ticketId || gate.ticketId}`);
          return;
        }
      } catch (_e) {
        // transient read error — keep polling until the cap
      }
      if (gateAttemptsRef.current >= GATE_POLL_MAX_ATTEMPTS) {
        stopGatePoll();
        setGateBusy(false);
        setGateError('Preparing the update is taking longer than expected. Please try again in a moment.');
      }
    }, GATE_POLL_MS);
  }, [claimId, stopGatePoll]);

  // Submission is async (API queues a Pub/Sub job → worker submits to Optum and
  // moves the ticket to 'submitted' / 'submission_failed'). Poll the ticket so we
  // can surface the real outcome instead of silently leaving the page.
  const stopSubmitPoll = useCallback(() => {
    if (submitPollRef.current) {
      clearInterval(submitPollRef.current);
      submitPollRef.current = null;
    }
  }, []);

  const finishSubmit = useCallback((t) => {
    stopSubmitPoll();
    setSubmitting(false);
    if (t.status === 'submitted') {
      setSubmitResult({
        severity: 'success',
        message: t.ticket_number
          ? `Appeal submitted to Optum. Reference #${t.ticket_number}.`
          : 'Appeal submitted to Optum successfully.',
      });
      return;
    }
    // A live CLAIM_ACTION_NOT_CREATABLE failure is NOT a dead end: Optum already
    // has a ticket, so route into the same reconcile-then-update path (the failed
    // ticket IS the one to reconcile) instead of showing an error the user can't act on.
    if (shouldReconcileOnFailure(t)) {
      setSubmitResult(null);
      goToUpdate({ ticketId: t.id, ticketReady: false });
      return;
    }
    setSubmitResult({
      severity: 'error',
      message: t.last_error_message
        ? `Submission failed: ${t.last_error_message}`
        : 'Submission failed. Review the details and try again.',
    });
  }, [stopSubmitPoll, goToUpdate]);

  const startSubmitPoll = useCallback((tid) => {
    stopSubmitPoll();
    submitAttemptsRef.current = 0;
    submitPollRef.current = setInterval(async () => {
      submitAttemptsRef.current += 1;
      try {
        const t = await getTicket(tid);
        applyTicket(t);
        if (SUBMIT_RESULT_STATUSES.has(t.status)) {
          finishSubmit(t);
          return;
        }
      } catch (_e) {
        // Transient read error — keep polling until max attempts.
      }
      if (submitAttemptsRef.current >= SUBMIT_POLL_MAX_ATTEMPTS) {
        stopSubmitPoll();
        setSubmitting(false);
        setSubmitResult({
          severity: 'info',
          message: 'Submission is still processing. Check the claim page in a moment for the final status.',
        });
      }
    }, SUBMIT_POLL_MS);
  }, [applyTicket, stopSubmitPoll, finishSubmit]);

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitResult(null);
    setSubmitBlock(null); // start each (re)try clean
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    const saved = await persistDraft({});
    if (!saved) {
      setSubmitting(false);
      return;
    }
    const tid = ticketIdRef.current;
    if (!tid) { setSubmitting(false); return; }
    setSaveError(null);
    try {
      const queued = await submitDraft(tid);
      // 202: ticket now 'submitting'. Reflect it and poll for the worker outcome.
      if (queued) applyTicket(queued);
      startSubmitPoll(tid);
    } catch (e) {
      const data = e.response?.data;
      // Slice C2: structured operator-incomplete block from the submit gate.
      if (data?.error_code === 'OPERATOR_PROFILE_INCOMPLETE') {
        setSubmitBlock({
          missing: Array.isArray(data.missing) ? data.missing : [],
          billing_provider_profile_id: data.billing_provider_profile_id || null,
        });
        setSubmitting(false);
        return;
      }
      const msg = data?.detail || 'Submission failed. Please try again.';
      setSubmitResult({ severity: 'error', message: msg });
      setSubmitting(false);
    }
  };

  // Poll for appeal letter until terminal status
  const startLetterPoll = useCallback(() => {
    if (letterPollRef.current) clearInterval(letterPollRef.current);
    letterPollRef.current = setInterval(async () => {
      try {
        const letters = await listClaimAppeals(claimId);
        const list = Array.isArray(letters) ? letters : [];
        setAppealLetters(list);
        const latest = list[0];
        if (latest && LETTER_TERMINAL_STATUSES.has(latest.status)) {
          clearInterval(letterPollRef.current);
          letterPollRef.current = null;
          if (latest.status === 'generated') {
            // Track the active appeal id (letterId for the structured panel) and
            // flip generationStatus so LetterEditor bumps the panel refresh key
            // once — the panel owns section content, so we no longer diff blobs.
            setActiveAppeal((prev) => (prev && prev.id === latest.id ? prev : latest));
            setLetterGenStatus('generated');
          } else {
            setLetterGenStatus('idle');
          }
        }
      } catch (_e) {
        // ignore poll errors
      }
    }, LETTER_POLL_MS);
  }, [claimId]);

  const handleGenerateAppeal = async () => {
    if (generatingLetter) return;
    setGeneratingLetter(true);
    setLetterError(null);
    setLetterNotice(null);
    try {
      let templates = appealTemplates;
      if (templates.length === 0) {
        const config = await getAppealTemplateConfig().catch(() => ({ templates: [] }));
        templates = Array.isArray(config?.templates) ? config.templates : [];
        setAppealTemplates(templates);
      }
      const selectedTemplate = selectAppealTemplateByCategory(templates, denialCategory);
      if (!selectedTemplate?.id) {
        setLetterError('No appeal templates are installed. Please seed the backend appeal templates.');
        return;
      }
      await generateAppealLetter({ claimId, templateId: selectedTemplate.id, model: 'gemini-2.5-flash' });
      setLetterNotice(`Generating appeal letter using "${selectedTemplate.name}". It will appear below when ready.`);
      setLetterGenStatus('generating');
      startLetterPoll();
    } catch (e) {
      setLetterError(e?.response?.data?.detail || e?.response?.data?.error || 'Could not start appeal letter generation.');
    } finally {
      setGeneratingLetter(false);
    }
  };

  // After LetterEditor fires a per-section regenerate against the appeal
  // generate endpoint, flip generationStatus and (re)start polling so the
  // panel shows "Generating…" and refetches sections once on completion. The
  // legacy blob save (updateAppealContent) is gone — LetterSectionsPanel
  // autosaves each section via patchLetterSection.
  const handleLetterGenerationStart = useCallback(() => {
    setLetterGenStatus('generating');
    setLetterError(null);
    startLetterPoll();
  }, [startLetterPoll]);

  // Document library: toggle a doc and autosave
  const handleToggleLibraryDoc = async (docId) => {
    const id = String(docId);
    // Removing: just deselect. The doc stays attached to the claim.
    if (selectedDocIdsRef.current.has(id)) {
      setSelectedDocIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      scheduleAutosave();
      return;
    }
    // Adding: reflect the selection immediately, then attach to THIS claim. A
    // Patient Library doc is patient-wide and may belong to another of the
    // patient's claims; the appeal submit path requires it to be attached here.
    setSelectedDocIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    try {
      await attachDocumentToClaim(claimId, id);
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      // "already attached" is fine; any other failure rolls back the selection.
      if (!/already attached/i.test(detail)) {
        setSelectedDocIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        setSaveError('Could not attach the document to this claim. Please try again.');
        return;
      }
    }
    // Refresh the claim-attached list so the doc shows under Supporting Evidence.
    try {
      const docs = await listClaimDocuments(claimId);
      setSupportingDocs(Array.isArray(docs) ? docs : []);
    } catch {
      // Non-fatal: the doc is attached; the claim-doc list refreshes on next load.
    }
    // Autosave only after a successful attach, so we never persist an unattached doc.
    scheduleAutosave();
  };

  const reasonChoices = deriveReasonChoices(ticket);
  const isSubmittable = appealStrategy === 'reconsideration' && Object.keys(selectedLines).length > 0;
  const strategyBlocksSubmit = appealStrategy && appealStrategy !== 'reconsideration';
  const isTerminal = ticket && LOCKED_STATUSES.has(ticket.status);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
  }

  // Hard-gate: when the claim already has an open reconsideration, replace the
  // entire appeal form with the ExistingReconGate panel (never offer Create).
  const gate = decideGate(eligibility);
  if (gate.gate) {
    return (
      <ExistingReconGate
        existingTicket={eligibility?.existing_ticket}
        busy={gateBusy}
        error={gateError}
        onPrimary={() => goToUpdate(gate)}
      />
    );
  }

  const lines = linesData?.lines || [];
  const claimLevelCodes = linesData?.claim_level_denial_codes || [];
  const denialCategory = linesData?.denial_category || '';
  const recommendation = linesData?.recommendation || {};

  // Resolve patient id for library modal
  const patientId = claim?.patient_id || claim?.patient || null;
  const providerId = claim?.provider_id || null;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Line-Item Appeal
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {saving && <Typography variant="caption" color="text.secondary">Saving…</Typography>}
          {ticket?.status && (
            <Chip label={ticket.status} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
          )}
          <Button size="small" onClick={() => setDiscardOpen(true)} disabled={isTerminal || submitting}>
            Discard
          </Button>
          <Button size="small" variant="outlined" onClick={handleSaveAndClose} disabled={isTerminal || submitting}>
            Save &amp; Close
          </Button>
        </Box>
      </Box>

      {saveError && <Alert severity="warning" sx={{ mb: 2 }}>{saveError}</Alert>}
      {submitting && ticket?.status === 'submitting' && (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 2 }}>
          Submitting to Optum… this may take a moment.
        </Alert>
      )}
      {submitResult && (
        <Alert
          severity={submitResult.severity}
          sx={{ mb: 2 }}
          action={
            submitResult.severity === 'success' ? (
              <Button color="inherit" size="small" onClick={() => navigate(`/claims/${claimId}`)}>
                Back to Claim
              </Button>
            ) : (
              <Button color="inherit" size="small" onClick={() => setSubmitResult(null)}>
                Dismiss
              </Button>
            )
          }
        >
          {submitResult.message}
        </Alert>
      )}
      {submitBlock && (
        <CompleteProfilePrompt
          missing={submitBlock.missing}
          billingProviderProfileId={submitBlock.billing_provider_profile_id}
          onRetry={handleSubmit}
          retrying={submitting}
        />
      )}
      {isTerminal && !submitResult && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This ticket is {ticket.status} and can no longer be edited.
        </Alert>
      )}

      {/* Section 1: Claim Identification (read-only) */}
      <Paper
        variant="outlined"
        sx={{ p: 3, mb: 3, borderColor: 'var(--ab-border-soft, #A0ABBB)', borderRadius: 2, bgcolor: 'action.hover' }}
      >
        <SectionHeader number={1} title="Claim Identification" />
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
          {[
            ['Patient', claim?.patient_display_name],
            // Same order as claims/claim_numbers.py::get_claim_reference_number,
            // which decides the number the generated appeal itself quotes: the
            // payer's own, then ours (837P CLM01), and only then the patient's
            // account number — which is shared by every claim for that patient.
            ['Claim Number', claim?.payer_claim_number || claim?.submitter_control_number
              || claim?.patient_account_number || claimId],
            ['Payer', claim?.payer_snapshot?.name],
            ['Service Date', claim?.service_date_from],
            ['Status', claim?.effective_status],
            ['Billing NPI', claim?.billing_provider_snapshot?.npi],
          ].map(([label, value]) => (
            <Box key={label}>
              <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
              <Typography variant="body2" sx={{ fontFamily: ['Claim Number', 'Billing NPI'].includes(label) ? 'monospace' : 'inherit', textTransform: label === 'Status' ? 'capitalize' : 'none' }}>
                {value || '—'}
              </Typography>
            </Box>
          ))}
        </Box>
        <BillingProviderPicker
          currentProfile={claim?.billing_provider_profile}
          disabled={!!isTerminal}
          onSave={handleBillingProviderSave}
        />
      </Paper>

      {/* Section 2: Line Items */}
      <Section number={2} title="Select Service Lines to Appeal">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Select the claim lines you want to include in this reconsideration.
        </Typography>
        {lines.length === 0 ? (
          <Alert severity="info">No appealable service lines found for this claim.</Alert>
        ) : (
          <>
            <LineItemsTable
              lines={lines}
              selectedLines={selectedLines}
              onLineToggle={handleLineToggle}
              disabled={!!isTerminal}
              normalizedGroups={linesData?.normalized_line_groups || []}
            />
            <InDisputeBar lines={lines} selectedLines={selectedLines} />
          </>
        )}
      </Section>

      {/* Section 3: Denial Context */}
      {(denialCategory || claimLevelCodes.length > 0) && (
        <Section number={3} title="Denial Context">
          {denialCategory && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2">
                <strong>Denial Category: </strong>
                <span style={{ textTransform: 'capitalize' }}>{denialCategory.replace(/_/g, ' ')}</span>
              </Typography>
            </Box>
          )}
          {claimLevelCodes.length > 0 && (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Claim-Level Reason Codes:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {claimLevelCodes.map((c) => (
                  <Chip
                    key={c.id}
                    label={`${c.code_type}-${c.code}`}
                    title={c.description}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
        </Section>
      )}

      {/* Section 4: Supporting Evidence */}
      <Section number={4} title="Supporting Evidence">
        {/* Claim-attached docs */}
        {supportingDocs.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            No documents attached to this claim yet. Upload supporting records from the claim page before submitting.
          </Typography>
        ) : (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Select documents to include with this appeal submission.
            </Typography>
            {supportingDocs.map((doc) => {
              const docId = doc.document_id || doc.id;
              const checked = selectedDocIds.has(String(docId));
              return (
                <Box
                  key={doc.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  <Checkbox
                    size="small"
                    checked={checked}
                    disabled={isTerminal}
                    onChange={() => {
                      setSelectedDocIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(String(docId))) next.delete(String(docId));
                        else next.add(String(docId));
                        return next;
                      });
                      scheduleAutosave();
                    }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap>{doc.filename || doc.file_name || 'Document'}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                      {(doc.document_type || '').replace(/_/g, ' ')}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Library modal button — only if patient id is available */}
        {patientId && (
          <Button
            size="small"
            variant="outlined"

            onClick={() => setLibraryOpen(true)}
            disabled={!!isTerminal}
            sx={{ mt: 0.5 }}
          >
            Choose from Library
          </Button>
        )}

        <DocumentLibraryModal
          open={libraryOpen}
          onClose={() => setLibraryOpen(false)}
          patientId={patientId}
          providerId={providerId}
          selectedDocIds={selectedDocIds}
          onToggleDoc={handleToggleLibraryDoc}
        />
      </Section>

      {/* Section 5: Appeal Strategy */}
      <Section number={5} title="Appeal Strategy">
        {recommendation.recommended_strategy && (
          <Alert severity="info" sx={{ mb: 2 }} icon={false}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.25 }}>
              Recommended: {STRATEGY_LABELS[recommendation.recommended_strategy]}
              <ConfidenceDot confidence={recommendation.confidence} />
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {recommendation.nudge_copy}
            </Typography>
          </Alert>
        )}
        {appealStrategy === 'reconsideration' && reasonChoices.length > 0 && (
          <TextField
            select
            SelectProps={{ native: true }}
            label="Reconsideration reason"
            value={reconsiderationReason}
            onChange={(e) => handleReconsiderationReasonChange(e.target.value)}
            disabled={isTerminal}
            fullWidth
            sx={{ mb: 2, maxWidth: 560, '& .MuiInputBase-root': { bgcolor: 'var(--ab-bg-input, #F7F8F9)' } }}
          >
            {reasonChoices.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </TextField>
        )}
        <RadioGroup value={appealStrategy} onChange={(e) => handleStrategyChange(e.target.value)}>
          {Object.entries(STRATEGY_LABELS).map(([value, label]) => (
            <FormControlLabel
              key={value}
              value={value}
              control={<Radio size="small" disabled={isTerminal} />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">{label}</Typography>
                  {recommendation.recommended_strategy === value && (
                    <Chip
                      label="Recommended"
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  )}
                </Box>
              }
            />
          ))}
        </RadioGroup>
        {strategyBlocksSubmit && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>{STRATEGY_LABELS[appealStrategy]}</strong> appeals are tracked but not yet auto-submitted.
            You can save this draft for offline processing.
          </Alert>
        )}
      </Section>

      {/* Section 6: Appeal Narrative */}
      <Section number={6} title="Appeal Narrative">
        <TextField
          label="Provider Comments"
          multiline
          minRows={4}
          fullWidth
          value={providerComments}
          onChange={handleCommentsChange}
          disabled={isTerminal}
          placeholder="Summarize why the denied services were medically necessary and should be reimbursed…"
          sx={{ '& .MuiInputBase-root': { bgcolor: 'var(--ab-bg-input, #F7F8F9)' } }}
        />
      </Section>

      {/* Section 7: Appeal Letter Generation + Inline Editor */}
      <Section number={7} title="Generate Appeal Letter">
        {letterError && <Alert severity="error" sx={{ mb: 1.5 }}>{letterError}</Alert>}
        {letterNotice && <Alert severity="info" sx={{ mb: 1.5 }}>{letterNotice}</Alert>}

        {/* Letter list */}
        {appealLetters.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {appealLetters.map((letter) => (
              <Box
                key={letter.id}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}
              >
                <Box>
                  <Typography variant="body2">{letter.name || letter.template_name || 'Appeal Letter'}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                    {(letter.status || '').replace(/_/g, ' ')}
                    {letter.created_at ? ` · ${new Date(letter.created_at).toLocaleDateString()}` : ''}
                  </Typography>
                </Box>
                {(activeAppeal?.appeal_letter || letter.download_url || letter.view_url) ? (
                  <Button
                    size="small"
                    variant="outlined"
                    href={activeAppeal?.appeal_letter || letter.download_url || letter.view_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open
                  </Button>
                ) : (
                  <Chip label={letter.status || 'generating'} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                )}
              </Box>
            ))}
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Generate an AI-drafted appeal letter based on the denial context above. Review and edit before sending.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: activeAppeal ? 2 : 0 }}>
          <Button
            variant="outlined"
            onClick={handleGenerateAppeal}
            disabled={generatingLetter || isTerminal}
            startIcon={generatingLetter ? <CircularProgress size={14} /> : null}
          >
            {generatingLetter ? 'Generating…' : (activeAppeal ? 'Regenerate' : 'Generate Appeal Letter')}
          </Button>
        </Box>

        {/* Inline letter editor — appears after generation */}
        {activeAppeal && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Letter Content
            </Typography>
            <LetterEditor
              appealId={activeAppeal.id}
              claimId={claimId}
              templateId={selectAppealTemplateByCategory(appealTemplates, denialCategory)?.id || null}
              generationStatus={letterGenStatus}
              onGenerationStart={handleLetterGenerationStart}
              onAlert={(msg) => setLetterError(msg)}
            />
          </Box>
        )}
      </Section>

      <Divider sx={{ my: 2 }} />

      {/* Action Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button variant="outlined" onClick={handleSaveAndClose} disabled={isTerminal || submitting}>
          Save Draft &amp; Close
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={(!isSubmittable && !ticketIdRef.current) || isTerminal || submitting}
          sx={{ bgcolor: 'var(--ab-fg, #000)', color: 'var(--ab-fg-inverse, #fff)', '&:hover': { bgcolor: '#333' } }}
        >
          {submitting && <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />}
          {isSubmittable ? 'Continue to Submit' : 'Save Draft'}
        </Button>
      </Box>

      {/* Transient submission-outcome toast */}
      <Snackbar
        open={!!submitResult}
        autoHideDuration={submitResult?.severity === 'success' ? 6000 : null}
        onClose={(_e, reason) => { if (reason !== 'clickaway') setSubmitResult(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={submitResult?.severity || 'info'}
          variant="filled"
          onClose={() => setSubmitResult(null)}
          action={
            submitResult?.severity === 'success' ? (
              <Button color="inherit" size="small" onClick={() => navigate(`/claims/${claimId}`)}>
                Back to Claim
              </Button>
            ) : undefined
          }
        >
          {submitResult?.message}
        </Alert>
      </Snackbar>

      {/* Discard confirmation dialog */}
      <Dialog open={discardOpen} onClose={() => setDiscardOpen(false)}>
        <DialogTitle>Discard this appeal?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will cancel the appeal draft. The ticket will be marked as discarded and can no longer be submitted.
            Audit records will be preserved.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardOpen(false)}>Keep Editing</Button>
          <Button onClick={handleDiscard} color="error">Discard</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
